import { useMemo, useCallback } from "react";
import type { LabResult, LabReading } from "../../types";
import { useLaboratoryFiltering } from "../../hooks/useLaboratoryFiltering";
import { useDateFnsFormattersWithLocale } from "../../../../hooks";

export interface UseLabResultsListProps {
  results: LabResult[];
  selectedId?: string;
  selectedDate?: string;
}

const getReadingForDate = (result: LabResult, date: string): LabReading | undefined => {
  return result.readings.find((r) => r.date.startsWith(date));
};

export const useLabResultsList = ({ results, selectedId: _selectedId, selectedDate }: UseLabResultsListProps) => {
  const { format } = useDateFnsFormattersWithLocale();
  const readingForResult = useCallback(
    (result: LabResult): LabReading | undefined => {
      if (!selectedDate) return undefined;
      return getReadingForDate(result, selectedDate);
    },
    [selectedDate]
  );

  const dateFilteredResults = useMemo(() => {
    if (!selectedDate) return results;
    return results.filter((result) => getReadingForDate(result, selectedDate));
  }, [results, selectedDate]);

  const getReading = useCallback(
    (result: LabResult): LabReading | undefined => {
      if (!selectedDate) return result.readings[0];
      return getReadingForDate(result, selectedDate);
    },
    [selectedDate]
  );

  const filtering = useLaboratoryFiltering({ results: dateFilteredResults, getReading });

  const screeningDate = useMemo(() => {
    if (selectedDate) {
      return new Date(selectedDate + "T00:00:00");
    }
    return results.reduce<Date | null>((latest, r) => {
      const d = r.readings[0] ? new Date(r.readings[0].date) : null;
      if (!d) return latest;
      return !latest || d > latest ? d : latest;
    }, null);
  }, [results, selectedDate]);

  const formattedScreeningDate = useMemo(() => {
    return screeningDate ? format(screeningDate, "MM/dd/yyyy") : null;
  }, [format, screeningDate]);

  return {
    ...filtering,
    dateFilteredResults,
    screeningDate,
    formattedScreeningDate,
    totalTests: dateFilteredResults.length,
    readingForResult,
    getReading,
  };
};

export type UseLabResultsListReturn = ReturnType<typeof useLabResultsList>;
